/* ============================================================================
   dop-core.js — shared behaviour, as deferred custom elements
   ============================================================================
   Loaded once from layout/theme.liquid with `defer`. Nothing here blocks render
   and nothing here is a framework.

   Scope, decided by the JS bucket sort in docs/build/mockup-inventory.md §4:
   this file owns ONLY behaviour the approved design actually needs — the nav
   drawer and the two modals. Cart, variants, search and forms are Shopify's,
   and the mockup's `onsubmit="return false"` stubs are placeholders for that
   platform behaviour, not behaviour to reproduce.

   Every element here is multi-instance safe (queries within itself, never
   document-wide) and re-initialises in the theme editor, where sections are
   re-rendered on every settings change.
   ========================================================================== */

(() => {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* --- focus management -----------------------------------------------
     The mockup's modals set aria-hidden and toggled a class. They did not
     trap focus and did not return it, so a keyboard user tabbed straight
     out of the open dialog into the page behind it. Added here, flagged as
     invented in inferred-design-system.md §3. */

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const focusableWithin = (root) =>
    Array.from(root.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetWidth || el.offsetHeight || el.getClientRects().length,
    );

  /* --- scroll lock -----------------------------------------------------
     Reference-counted. The mockup set document.body.style.overflow directly,
     which breaks as soon as two things want the lock at once — close one and
     the page scrolls again while the other is still open. */

  let lockCount = 0;
  let savedOverflow = '';

  const lockScroll = () => {
    if (lockCount === 0) {
      savedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    lockCount += 1;
  };

  const unlockScroll = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.style.overflow = savedOverflow;
  };

  /* ==========================================================================
     <dop-modal> — the reveal and box-select dialogs
     ========================================================================== */

  class DopModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-dop-dialog]');
      this.closers = this.querySelectorAll('[data-dop-close]');
      this.opener = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.onBackdrop = this.onBackdrop.bind(this);

      this.closers.forEach((el) => el.addEventListener('click', () => this.close()));
      this.addEventListener('click', this.onBackdrop);

      // Openers live outside the modal (on the box cards), so they are found
      // within the owning SECTION rather than document-wide — two box sections
      // on one page must not drive each other's modal.
      const scope = this.closest('.shopify-section') || document;
      scope.querySelectorAll(`[data-dop-open="${this.id}"]`).forEach((btn) => {
        btn.addEventListener('click', (e) => this.open(btn, e));
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.hasAttribute('open')) unlockScroll();
    }

    onBackdrop(e) {
      if (e.target === this) this.close();
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = focusableWithin(this.dialog);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    open(opener) {
      this.opener = opener || null;
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      document.addEventListener('keydown', this.onKeydown);

      this.dispatchEvent(new CustomEvent('dop:modal:open', { bubbles: true, detail: { opener } }));

      const target = this.dialog.querySelector('[data-dop-autofocus]') || focusableWithin(this.dialog)[0];
      if (target) target.focus();
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);

      // Return focus to whatever opened it. Without this a keyboard user is
      // dropped back at the top of the document.
      if (this.opener && document.contains(this.opener)) this.opener.focus();
      this.opener = null;
    }
  }

  /* ==========================================================================
     <dop-nav> — the header hamburger drawer
     ========================================================================== */

  class DopNav extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-dop-nav-toggle]');
      this.menu = this.querySelector('[data-dop-nav-menu]');
      if (!this.toggle || !this.menu) return;

      this.onDocClick = this.onDocClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.toggle.addEventListener('click', () => this.setOpen(!this.isOpen));
      // Any nav link closes the drawer. The mockup wired onclick="closeNav()"
      // on each anchor; delegation does the same job without inline handlers,
      // which a strict CSP would block anyway.
      this.menu.addEventListener('click', (e) => {
        if (e.target.closest('a')) this.setOpen(false);
      });

      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    get isOpen() {
      return this.menu.classList.contains('is-open');
    }

    setOpen(open) {
      this.menu.classList.toggle('is-open', open);
      this.toggle.setAttribute('aria-expanded', String(open));
    }

    onDocClick(e) {
      if (this.isOpen && !this.contains(e.target)) this.setOpen(false);
    }

    onKeydown(e) {
      if (e.key === 'Escape' && this.isOpen) {
        this.setOpen(false);
        this.toggle.focus();
      }
    }
  }

  /* ==========================================================================
     <dop-reveal> — entrance animation, opt-in per section
     IntersectionObserver, not a scroll handler. Gated on reduced motion, which
     matters more than usual on a site sold as sensory-friendly.
     ========================================================================== */

  class DopReveal extends HTMLElement {
    connectedCallback() {
      if (REDUCED_MOTION.matches) {
        this.setAttribute('revealed', '');
        return;
      }
      this.observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.setAttribute('revealed', '');
            obs.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px' },
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  /* ==========================================================================
     <dop-cart> — the cart drawer
     ==========================================================================
     Added 2026-08-21. The approved design has no drawer and no cart entry
     point; both are recorded as additions in CLAUDE.md.

     It SUBCLASSES DopModal rather than reimplementing it, so the focus trap,
     Escape, backdrop click, focus return and the reference-counted scroll lock
     are the ones already proven on the box-select modal.

     Three things it deliberately does not do:

     1. It does not build cart rows in JavaScript. Markup comes from Liquid via
        the Section Rendering API, so money formatting, discounts and selling
        plan names have exactly one source of truth and the drawer can never
        disagree with the cart page.
     2. It does not replace the plain-form buy flow. Every form still posts to
        /cart/add on its own if this script never runs. The interception is an
        enhancement layered on top, and it disappears the moment JS fails.
     3. It does not swallow errors. If /cart/add rejects — sold out, a stale
        variant — the form is submitted for real so Shopify renders its own
        error rather than the shopper silently getting nothing. */

  class DopCart extends DopModal {
    connectedCallback() {
      super.connectedCallback();

      this.sectionId = this.getAttribute('data-section-id');
      this.content = this.querySelector('[data-dop-cart-content]');
      this.busy = false;
      // Separate from `busy`, which guards quantity changes. An add and a
      // change are different mutations, and blocking one on the other would
      // make the drawer feel stuck for no reason.
      this.adding = false;

      // Openers live anywhere on the page, not inside this section, so the
      // scoped lookup DopModal does for the box modals is widened here.
      this.onOpenerClick = this.onOpenerClick.bind(this);
      document.addEventListener('click', this.onOpenerClick);

      // Add to cart, from anywhere on the page.
      //
      // CLICK is the primary path and SUBMIT is only a fallback, which looks
      // backwards until you measure it. Shopify's web pixel counts a `submit`
      // on a /cart/add form as an add-to-cart signal AND separately reports the
      // AJAX /cart/add.js call. Intercepting the submit event still lets it be
      // observed, so one shopper click was reported to Meta TWICE - once thin,
      // from the form fields alone, and once complete, from the AJAX response.
      // Taking the click and preventing it means no submit event is ever
      // dispatched, so exactly one add is reported. Measured on the live store
      // 2026-09-02: 2 beacons before, 1 after, cart correct in both.
      //
      // The form is still a real form. With JS dead it posts to /cart/add on
      // its own and Shopify renders the cart page, which is decision 8 intact.
      this.onAddClick = this.onAddClick.bind(this);
      document.addEventListener('click', this.onAddClick);

      // Fallback for a submit that arrives without a click - scripted
      // requestSubmit(), or implicit submission in a form with no submit
      // button. Rare, and a correct cart matters more than a clean metric.
      this.onSubmit = this.onSubmit.bind(this);
      document.addEventListener('submit', this.onSubmit);

      // Quantity and remove, inside the drawer. Delegated, so they survive
      // every re-render without rebinding.
      this.onDrawerClick = this.onDrawerClick.bind(this);
      this.onDrawerChange = this.onDrawerChange.bind(this);
      this.addEventListener('click', this.onDrawerClick);
      this.addEventListener('change', this.onDrawerChange);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      document.removeEventListener('click', this.onOpenerClick);
      document.removeEventListener('click', this.onAddClick);
      document.removeEventListener('submit', this.onSubmit);
    }

    onOpenerClick(e) {
      const opener = e.target.closest('[data-dop-cart-open]');
      if (!opener) return;
      // Let a modified click do what the shopper actually asked for: open the
      // cart page in a new tab, not the drawer.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      this.open(opener);
    }

    /* The primary path. Takes the click before a submit event can exist. */
    onAddClick(e) {
      if (e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      const submitter = e.target.closest('button, input[type="submit"], input[type="image"]');
      if (!submitter) return;
      const type = (submitter.getAttribute('type') || 'submit').toLowerCase();
      if (type !== 'submit' && type !== 'image') return;
      const form = submitter.form || submitter.closest('form');
      if (!form || (form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;

      e.preventDefault();
      this.addFrom(form, submitter);
    }

    /* Fallback only. See connectedCallback for why this is not the main path. */
    onSubmit(e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;

      // Someone already handled this submit. Dawn's <product-form> binds a
      // listener on the FORM, which runs before this document-level one, and it
      // posts to /cart/add itself. Two handlers on one form is two adds and two
      // boxes to pay for. Nothing on the approved pages renders <product-form>,
      // but the customizer can drop a stock Dawn section onto any template at
      // any time, so this cannot depend on the current template set.
      if (e.defaultPrevented) return;

      e.preventDefault();
      this.addFrom(form, e.submitter);
    }

    async addFrom(form, submitter) {
      // A second add while the first is still in flight adds the box twice.
      // aria-busy below is presentation only; this is the actual guard. Double
      // tapping a buy button on a slow connection is ordinary behaviour, not an
      // edge case, and on a phone it is the common one.
      if (this.adding) return;
      this.adding = true;

      if (submitter) submitter.setAttribute('aria-busy', 'true');

      try {
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            body: new FormData(form),
            headers: { Accept: 'application/json' },
          });
          if (!res.ok) throw new Error('add failed');
        } catch (err) {
          // The ADD ITSELF failed - sold out, a stale variant, no network. Hand
          // it back to Shopify so it renders its own error rather than failing
          // quietly. Nothing was added, so submitting for real is safe here.
          form.submit();
          return;
        }

        // Past this line the box IS in the cart. Anything that throws from here
        // is a DRAWER problem, and resubmitting the form would add a SECOND box
        // to recover from a cosmetic failure. Send them to the cart page, which
        // shows the item that really was added.
        try {
          // Close whatever modal the buy button lived in before opening this one,
          // or two dialogs hold the scroll lock and the page stays frozen.
          const owning = form.closest('dop-modal[open]');
          if (owning && typeof owning.close === 'function') owning.close();
          await this.refresh();
          this.open(document.querySelector('[data-dop-cart-open]'));
        } catch (err) {
          window.location.href = '/cart';
        }
      } finally {
        this.adding = false;
        if (submitter) submitter.removeAttribute('aria-busy');
      }
    }

    onDrawerClick(e) {
      const remove = e.target.closest('[data-dop-cart-remove]');
      if (!remove) return;
      e.preventDefault();
      this.change(remove.getAttribute('data-line'), 0);
    }

    onDrawerChange(e) {
      const qty = e.target.closest('[data-dop-cart-qty]');
      if (!qty) return;
      const n = parseInt(qty.value, 10);
      this.change(qty.getAttribute('data-line'), Number.isFinite(n) && n >= 0 ? n : 1);
    }

    async change(line, quantity) {
      if (this.busy) return;
      this.busy = true;
      this.setAttribute('data-busy', 'true');
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity: quantity }),
        });
        if (!res.ok) throw new Error('change failed');
        await this.refresh();
      } catch (err) {
        // The cart page is the fallback for anything this cannot do.
        window.location.href = '/cart';
      } finally {
        this.busy = false;
        this.removeAttribute('data-busy');
      }
    }

    /* Re-render from Liquid through the Section Rendering API. */
    async refresh() {
      if (!this.sectionId || !this.content) return;
      const url = '/?sections=' + encodeURIComponent(this.sectionId);
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const data = await res.json();
      const html = data[this.sectionId];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-dop-cart-content]');
      if (next) this.content.innerHTML = next.innerHTML;

      const root = doc.querySelector('dop-cart');
      if (root) this.setAttribute('data-empty', root.getAttribute('data-empty') || 'false');

      // The dialog's focusable set just changed underneath the focus trap.
      this.dialog = this.querySelector('[data-dop-dialog]');
      await this.syncCount();
    }

    /* The header badge is in a different section. It is updated from the cart
       itself rather than by re-rendering the header, which would rebuild the
       nav underneath the shopper mid-interaction. */
    async syncCount() {
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
          return r.json();
        });
        document.querySelectorAll('[data-dop-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.classList.toggle('dop-header__cartcount--empty', cart.item_count === 0);
        });
        document.querySelectorAll('[data-dop-cart-label]').forEach(function (el) {
          const unit = cart.item_count === 1 ? 'item' : 'items';
          const name = (el.textContent || 'Cart').split(',')[0].trim();
          el.textContent = name + ', ' + cart.item_count + ' ' + unit;
        });
      } catch (err) {
        /* a stale badge is not worth breaking the drawer over */
      }
    }
  }

  /* --- registration ----------------------------------------------------
     Guarded, because the theme editor re-runs section scripts and a second
     define() of the same name throws. */

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  define('dop-modal', DopModal);
  define('dop-nav', DopNav);
  define('dop-reveal', DopReveal);
  define('dop-cart', DopCart);

  // Expose the shared utilities for section-specific modules that need them,
  // so the scroll-lock counter stays single-source.
  window.DopCore = { focusableWithin, lockScroll, unlockScroll, REDUCED_MOTION };
})();
